/**
 * Loads the Razorpay checkout script dynamically.
 * Returns true on success, false if the script fails to load.
 */
export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Unified payment gateway dispatcher.
 *
 * Inspects `response.gateway` and routes to PayU (form redirect)
 * or Razorpay (JS SDK popup). If gateway is absent, defaults to PayU.
 *
 * Accepted response shapes:
 *   PayU:     { gateway: 'payu', payu_url | payment_url, payment_data, ... }
 *   Razorpay: { gateway: 'razorpay', order_id, key, amount (paise), currency, ... }
 *
 * @param {object} response   Payment initiation response from the backend.
 * @param {object} [options]
 *   @param {function} [options.onRazorpaySuccess]  Called with (rzpPayment, response) after handler fires.
 *   @param {function} [options.onRazorpayCancel]   Called when popup is dismissed without payment.
 *   @param {function} [options.onRazorpayFail]     Called with (errorDescription) on payment.failed event.
 *   @param {function} [options.onSdkError]         Called when the Razorpay SDK fails to load.
 *   @param {object}   [options.prefill]            Razorpay prefill: { name, email, contact }.
 */
export async function handlePaymentGateway(response, options = {}) {
  const { onRazorpaySuccess, onRazorpayCancel, onRazorpayFail, onSdkError, prefill = {} } = options;

  const gateway = response?.gateway || 'payu';

  // ─── PayU: hidden form redirect ──────────────────────────────────────────
  if (gateway === 'payu') {
    const payuUrl = response.payu_url || response.payment_url;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payuUrl;
    Object.entries(response.payment_data || {}).forEach(([key, val]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = val ?? '';
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    return;
  }

  // ─── Razorpay: JS SDK popup ──────────────────────────────────────────────
  if (gateway === 'razorpay') {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      onSdkError?.();
      return;
    }

    const rzpOptions = {
      key: response.key,
      // Paise, echoed from the Razorpay order so it always matches it.
      // `order_id` is authoritative anyway; this is belt-and-braces.
      amount: response.amount,
      currency: response.currency || 'INR',
      order_id: response.order_id,
      handler: function (rzpPayment) {
        onRazorpaySuccess?.(rzpPayment, response);
      },
      modal: {
        ondismiss: function () {
          onRazorpayCancel?.();
        }
      },
      prefill
    };

    const rzp = new window.Razorpay(rzpOptions);
    rzp.on('payment.failed', function (rzpResponse) {
      onRazorpayFail?.(rzpResponse?.error?.description || 'Payment failed.');
    });
    rzp.open();
  }
}
